import { motion, useReducedMotion, useScroll } from "framer-motion";
import { useRef } from "react";

import { useParallax } from "../hooks/useParallax";

import { SanskritTerm } from "./SanskritTerm";
import { ScrollReveal } from "./ScrollReveal";
import { SectionHead } from "./SectionHead";

/**
 * The name — vihangam, "the path of the bird". The birds carved in
 * stone at the Mahamandir sit beside the words that explain them,
 * and the bird's arc draws itself across the section as the reader
 * scrolls through. The only ornament on the site.
 */
export function Invitation() {
  const sectionRef = useRef<HTMLElement>(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start 0.9", "end 0.6"],
  });
  const plate = useParallax(22);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-cream-dark px-7 py-24 md:py-32"
    >
      <svg
        className="pointer-events-none absolute -top-8 left-1/2 w-[140%] max-w-none -translate-x-1/2 md:top-0"
        viewBox="0 0 1400 420"
        fill="none"
        aria-hidden
      >
        <motion.path
          d="M-40 400 Q 700 -160 1440 320"
          stroke="var(--color-gold)"
          strokeWidth="1"
          opacity="0.16"
          style={reduce ? undefined : { pathLength: scrollYProgress }}
        />
      </svg>

      <div className="relative mx-auto max-w-6xl">
        <SectionHead index="02" label="The Name" />

        <div className="mt-14 grid items-center gap-12 md:mt-20 md:grid-cols-12 md:gap-14">
          <div className="space-y-8 md:col-span-7">
            <ScrollReveal>
              <p className="text-base leading-[1.8] text-warm-brown-light md:text-[1.0625rem]">
                For over a century, a practice has been quietly transforming
                lives across the world.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={0.15}>
              <h2 className="font-display text-3xl leading-[1.25] text-warm-brown md:text-[2.2rem]">
                <SanskritTerm
                  term="Vihangam Yog"
                  devanagari="विहंगम योग"
                  pronunciation="vi-hung-um yog"
                  meaning="The science of consciousness — the path of the soul that soars like a bird beyond the material world."
                  lore="'Vihangam' means 'like a bird.' Just as a bird flies effortlessly above the earth, the soul transcends the physical realm through this practice."
                />{" "}
                is not yoga as you know it.
              </h2>
            </ScrollReveal>

            <ScrollReveal delay={0.25}>
              <p className="max-w-xl text-base leading-[1.8] text-warm-brown-light md:text-[1.0625rem]">
                It&rsquo;s not postures or breathing exercises. It is{" "}
                <SanskritTerm
                  term="Yog"
                  devanagari="योग"
                  pronunciation="yog"
                  meaning="Union — the science of uniting individual consciousness with the Supreme. Distinct from physical yoga (postures and exercises)."
                  lore="In the West, 'yoga' typically refers to physical postures (asana). Yog in its true sense is the complete science of inner union — far beyond the physical."
                />{" "}
                in its older sense — a meditation taught in plain language, ten
                minutes in the morning and ten in the evening.
              </p>
            </ScrollReveal>
          </div>

          <ScrollReveal delay={0.2} className="md:col-span-5">
            <motion.div ref={plate.ref} style={{ y: plate.y }}>
              <figure className="plate">
                <img
                  src="/images/carvings/carving-vihangam-birds.jpg"
                  alt="Stone carving of birds in flight from the Swarved Mahamandir — symbolising the soul that soars beyond"
                  className="w-full object-cover"
                />
                <figcaption className="plate-caption">
                  The path of the bird · carved at the Mahamandir
                </figcaption>
              </figure>
            </motion.div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
